import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Snowflake, Package, Wrench, Zap, ArrowUpRight, X, type LucideIcon } from 'lucide-react'
import { Section } from '../ui/Section'
import { Container } from '../ui/Container'
import { SectionTitle } from '../ui/SectionTitle'

type CategoryKey = 'refrigerated' | 'curtain' | 'special' | 'expressVans'

interface CategoryDef {
  key: CategoryKey
  icon: LucideIcon
  image: string
}

const categories: CategoryDef[] = [
  { key: 'refrigerated', icon: Snowflake, image: '/images/fleet-refrigerated.png' },
  { key: 'curtain', icon: Package, image: '/images/fleet-curtain.png' },
  { key: 'special', icon: Wrench, image: '/images/fleet-special.png' },
  { key: 'expressVans', icon: Zap, image: '/images/fleet-express.png' },
]

interface VehicleEntry {
  title: string
  specs: string
}

export function FleetSection() {
  const { t } = useTranslation('common')
  const [activeKey, setActiveKey] = useState<CategoryKey | null>(null)

  const active = activeKey ? categories.find((c) => c.key === activeKey) ?? null : null

  useEffect(() => {
    if (!activeKey) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveKey(null)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [activeKey])

  const vehicles = active
    ? (t(`company.fleet.categories.${active.key}.vehicles`, {
        returnObjects: true,
      }) as VehicleEntry[])
    : []

  return (
    <Section variant="light">
      <Container>
        <SectionTitle
          title={t('company.fleet.heading')}
          subtitle={t('company.fleet.body')}
          centered
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => {
            const Icon = category.icon
            const title = t(`company.fleet.categories.${category.key}.title`)
            const description = t(`company.fleet.categories.${category.key}.description`)
            return (
              <motion.button
                key={category.key}
                type="button"
                onClick={() => setActiveKey(category.key)}
                aria-label={t('company.fleet.aria.openDetail', { title })}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -6 }}
                className="group relative flex flex-col overflow-hidden rounded-lg bg-white text-left shadow-sm transition-shadow duration-normal hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-lg bg-[#F9F6F1]">
                  <img
                    src={category.image}
                    alt={title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute left-4 top-4 inline-flex h-12 w-12 items-center justify-center rounded-md bg-white text-brand-red shadow-md transition-all duration-normal group-hover:bg-brand-red group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>
                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand-red transition-transform duration-normal group-hover:scale-x-100" />

                <div className="relative flex flex-1 flex-col p-6">
                  <h3 className="mb-2 text-lg font-semibold text-brand-dark">{title}</h3>
                  <p className="text-sm leading-relaxed text-brand-text/80">{description}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-red opacity-0 transition-all duration-normal group-hover:translate-x-1 group-hover:opacity-100">
                    {t('company.fleet.labels.discover')}
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </motion.button>
            )
          })}
        </div>
      </Container>

      <AnimatePresence>
        {active && (
          <motion.div
            key="fleet-modal"
            className="fixed inset-0 z-modal flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="fleet-modal-title"
            onClick={() => setActiveKey(null)}
          >
            <div className="absolute inset-0 bg-brand-dark/70 backdrop-blur-sm" />

            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl"
            >
              <div className="flex items-start justify-between gap-4 bg-brand-red px-8 py-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-white/15">
                    <active.icon className="h-6 w-6" />
                  </div>
                  <h3 id="fleet-modal-title" className="text-xl font-semibold md:text-2xl">
                    {t(`company.fleet.categories.${active.key}.title`)}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveKey(null)}
                  aria-label={t('company.fleet.aria.closeDetail')}
                  className="-mr-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[calc(90vh-96px)] space-y-6 overflow-y-auto px-8 py-7">
                <p className="text-base leading-relaxed text-brand-text">
                  {t(`company.fleet.categories.${active.key}.description`)}
                </p>

                {Array.isArray(vehicles) && vehicles.length > 0 && (
                  <div>
                    <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-dark">
                      {t('company.fleet.labels.vehicles')}
                    </h4>
                    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {vehicles.map((vehicle) => (
                        <li
                          key={vehicle.title}
                          className="flex gap-4 rounded-md border border-brand-dark/10 bg-[#F9F6F1] p-4"
                        >
                          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-red/10 text-brand-red">
                            <active.icon className="h-5 w-5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <h5 className="mb-2 text-sm font-semibold text-brand-dark">
                              {vehicle.title}
                            </h5>
                            <p className="whitespace-pre-line text-xs leading-relaxed text-brand-text/80">
                              {vehicle.specs}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  )
}
